import {inject} from '@angular/core';
import {Observable, of, share, switchMap} from 'rxjs';
import {EntityApiService, ListApiOptions} from './entity-api.service';
import {CreateEntity, Entity, EntityId, EntityModel, isEntityId, PagedList} from '../models/entity.model';
import {OrganizationService} from './organization.service';

type WithOrgIdCallback<T> = (orgId: string) => Observable<T>;

/**
 * Base class for entity services that are scoped to organizations.
 * Automatically defaults to the active organization when no org ID is provided.
 *
 * @example
 * ```typescript
 * // All these work:
 * service.list()                          // Uses active org
 * service.list({ pageSize: 10 })          // Uses active org with options
 * service.list('org-id')                  // Uses specific org
 * service.list('org-id', { pageSize: 10 }) // Uses specific org with options
 * ```
 */
export abstract class OrganizationEntityApiService<
  T extends Entity,
  TModel extends EntityModel<T>,
  TCreate = CreateEntity<T>
> extends EntityApiService<T, TModel, TCreate> {

  protected readonly orgService = inject(OrganizationService);

  /**
   * List entities. Defaults to active organization if no orgId provided.
   * @param orgId - Organization ID. If omitted, uses active org.
   * @param options - List options (only when first param is orgId)
   */
  override list(orgId: EntityId, options?: ListApiOptions): Observable<PagedList<TModel>>;
  override list(options?: ListApiOptions): Observable<PagedList<TModel>>;
  override list(orgIdOrOptions?: EntityId | ListApiOptions, options?: ListApiOptions): Observable<PagedList<TModel>> {
    if (isEntityId(orgIdOrOptions)) {
      // First param is orgId
      return this.resolveOrgId(orgIdOrOptions).pipe(
        switchMap(orgId => super.list(orgId, options))
      )
    } else {
      // First param is options
      return this.resolveOrgId().pipe(
        switchMap(orgId => super.list(orgId, orgIdOrOptions))
      )
    }
  }

  /**
   * Create a new entity. Defaults to active organization if no orgId provided.
   * @param request - Create request (only when first param is orgId)
   */
  override create(request: TCreate): Observable<TModel>
  override create(scope: never, request: never): Observable<TModel>
  override create(request: TCreate): Observable<TModel> {
    return this.withOrgId(orgId => super.create(orgId, request));
  }

  protected withOrgId<T>(callback: WithOrgIdCallback<T>): Observable<T> {
    return this.orgService.getActiveOrgId().pipe(switchMap(orgId => callback(orgId)), share());
  }

  /**
   * Resolve org ID from parameter or use active org
   */
  protected resolveOrgId(orgIdOrOptions?: EntityId | ListApiOptions): Observable<string> {
    if (!orgIdOrOptions) {
      return this.orgService.getActiveOrgId();
    }
    if (typeof orgIdOrOptions === 'string') {
      return of(orgIdOrOptions);
    }
    if ('id' in orgIdOrOptions) {
      return of(orgIdOrOptions.id);
    }
    return this.orgService.getActiveOrgId();
  }
}
